import { FC, useEffect, useState } from 'react';
import { Box, LinearProgress, Snackbar, Typography } from '@mui/joy';
import { useSelector } from 'react-redux';
import { RootState } from '_services/Redux/store';

const statusMessages = [
  'Processing files...',
  'Extracting content...',
  'Structuring data...',
  'Almost done',
];

/**
 * The upload-progress snackbar, and the only holder of the upload-slice
 * subscription it needs. This used to live in MainLayout, which renders the
 * whole routed page as `children` — so every distinct progress percentage
 * (Uppy emits integers 0-100) re-rendered the entire page. Keeping the
 * subscription in this leaf confines those re-renders to the snackbar.
 */
const UploadProgressSnackbar: FC = () => {
  const { progress, isUploading } = useSelector(
    (state: RootState) => state.upload
  );
  const [status, setStatus] = useState('Uploading files...');

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isUploading && progress >= 100) {
      let localIndex = 0;
      setStatus(statusMessages[localIndex]);

      interval = setInterval(() => {
        localIndex += 1;

        if (localIndex >= statusMessages.length) {
          clearInterval(interval);
          return;
        }

        setStatus(statusMessages[localIndex]);
      }, 20000);
    } else if (isUploading) {
      setStatus(`Uploading files... ${Math.round(progress)}%`);
    }

    return () => {
      clearInterval(interval);
    };
  }, [progress, isUploading]);

  return (
    <Snackbar
      variant="outlined"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      open={isUploading && progress > 0}
      sx={{
        minWidth: { xs: '90vw', sm: 300 },
        maxWidth: { xs: '95vw', sm: 'unset' },
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography level="body-sm" sx={{ mb: 1 }}>
          {progress >= 100 ? (
            <Typography level="body-sm" sx={{ mb: 1 }}>
              {`${status} ${Math.round(progress)}%`}
            </Typography>
          ) : (
            `Uploading files... ${Math.round(progress)}%`
          )}
        </Typography>
        <LinearProgress determinate value={progress >= 100 ? 100 : progress} />
      </Box>
    </Snackbar>
  );
};

export default UploadProgressSnackbar;
